"use client";

import React from "react";
import { motion } from "framer-motion";
import { Phone, Clock, ShieldCheck, MapPin, CircleAlert as AlertCircle, Building2 } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  icon: React.ElementType;
}
const faqs: FaqItem[] = [{
  id: "item-1",
  question: "What is your typical emergency response time in Baltimore County?",
  answer: "TriServe maintains an average 60-minute emergency mobile deployment window across Baltimore County. Our response teams navigate directly along key transport arteries—including the I-695 Beltway, I-83 corridor, and I-95—with fully stocked mitigation vehicles carrying commercial extractors, air movers, and moisture meters for immediate containment.",
  icon: Clock
}, {
  id: "item-2",
  question: "Which geographic areas of Baltimore County do you serve?",
  answer: "We provide comprehensive disaster restoration across all unincorporated and municipal sectors of Baltimore County. Routine deployment zones include Towson, Catonsville, Dundalk, Owings Mills, Perry Hall, White Marsh, Essex, Arbutus, Parkville, Cockeysville, and Middle River, addressing specific regional structural risks from coastal humidity to regional clay soil saturation.",
  icon: MapPin
}, {
  id: "item-3",
  question: "How does TriServe coordinate with insurance adjusters and handle claims?",
  answer: "Our field leads work directly alongside your insurance adjuster throughout mitigation and rebuild phases. We provide comprehensive moisture mapping logs, thermal imaging captures, and line-item documentation formatted to industry-standard estimating platforms, ensuring smooth communication and clear justification for covered repairs.",
  icon: ShieldCheck
}, {
  id: "item-4",
  question: "What immediate steps should a resident take after discovering water, fire, or mold damage?",
  answer: "Prioritize life safety first: immediately shut off the main water line for active plumbing bursts or isolate electrical breakers in waterlogged areas. In older structures around Towson and Catonsville with plaster or finished basements, avoid entering rooms with sagging ceilings. Do not disturb visible mold colonies, as airborne spores can quickly spread through HVAC ducts. Contact our dispatch line immediately for stabilization guidance.",
  icon: AlertCircle
}, {
  id: "item-5",
  question: "Can I visit a local TriServe office in Baltimore County?",
  answer: "TriServe operates strictly through rapid mobile dispatch units and does not maintain a public walk-in customer office in Maryland. This decentralized operational model ensures all certified technicians, industrial dehumidifiers, and thermal imaging equipment stay pre-loaded and mobile in field trucks, minimizing deployment delays across Baltimore County communities.",
  icon: Building2
}, {
  id: "item-6",
  question: "Is emergency service available 24/7, and how do I contact dispatch?",
  answer: "Yes, our emergency disaster crews are ready for dispatch 24 hours a day, 7 days a week, 365 days a year. You can connect directly with local emergency personnel at 855-435-6631 for urgent water extraction, structural fire dry-out, sewage backup cleanup, or containment.",
  icon: Phone
}];
export default function CountyFaq() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer
      }
    }))
  };
  return <section id="baltimorecountyrestorationfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border transition-colors duration-200">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        ease: "easeOut"
      }} className="text-center mb-10 sm:mb-14">
          <div className="flex justify-center mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold px-3 py-1 border-border bg-muted/50 text-foreground">
              Local Dispatch Intel
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground">
            Baltimore County Restoration FAQs
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Practical answers regarding emergency dispatch times, regional coverage across the I-695 Beltway, and direct insurance claims coordination.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.32,
        delay: 0.1,
        ease: "easeOut"
      }}>
          <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
            {faqs.map((faq, index) => {
            const Icon = faq.icon;
            return <AccordionItem key={faq.id} value={faq.id} data-index={index} className="border border-border rounded-lg bg-card px-4 sm:px-6 py-1 transition-all duration-200 shadow-xs hover:border-foreground/20">
                  <AccordionTrigger className="text-base sm:text-lg font-medium text-foreground hover:no-underline py-4 text-left">
                    <span className="flex items-center gap-3 pr-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span>{faq.question}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed pl-11 pb-5">
                    {faq.id === "item-6" ? <p>
                        Yes, our emergency disaster crews are ready for dispatch 24 hours a day, 7 days a week, 365 days a year. You can connect directly with local emergency personnel at{" "}
                        <a href="tel:855-435-6631" className="font-medium text-foreground underline hover:text-primary transition-colors inline-flex items-center gap-1">
                          855-435-6631
                        </a>{" "}
                        for urgent water extraction, structural fire dry-out, sewage backup cleanup, or containment.
                      </p> : <p>{faq.answer}</p>}
                  </AccordionContent>
                </AccordionItem>;
          })}
          </Accordion>
        </motion.div>

        <div className="mt-12 text-center text-sm text-muted-foreground border-t border-border pt-6">
          <p>
            Facing an immediate structural hazard in Baltimore County? Call 24/7 dispatch directly at{" "}
            <a href="tel:855-435-6631" className="font-medium text-foreground underline hover:text-primary transition-colors">
              855-435-6631
            </a>
            .
          </p>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} />
    </section>;
}